import { useSyncExternalStore } from 'react';
import type { AppLocale } from '../i18n/i18n';
import {
  DIGIT_SYSTEM_PREFERENCE_KEY,
  DISPLAY_CALENDAR_PREFERENCE_KEY,
  DISPLAY_PREFERENCES_EVENT,
  IRAN_CURRENCY_DISPLAY_PREFERENCE_KEY,
  getCalendarForLocale,
  getDigitSystemForLocale,
  getDigitSystemPreference,
  getDisplayCalendarPreference,
  getIranCurrencyDisplayPreference,
  setDigitSystemPreference,
  setDisplayCalendarPreference,
  setIranCurrencyDisplayPreference,
} from './calendar';

function subscribe(listener: () => void): () => void {
  const onStorage = (event: StorageEvent) => {
    if (
      event.key === DISPLAY_CALENDAR_PREFERENCE_KEY
      || event.key === DIGIT_SYSTEM_PREFERENCE_KEY
      || event.key === IRAN_CURRENCY_DISPLAY_PREFERENCE_KEY
    ) listener();
  };
  window.addEventListener(DISPLAY_PREFERENCES_EVENT, listener);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener(DISPLAY_PREFERENCES_EVENT, listener);
    window.removeEventListener('storage', onStorage);
  };
}

/**
 * Reactive device display preferences shared by the classic and modern shells. Locale supplies
 * the first value only; once established, changing language never silently changes the calendar,
 * digit system, or Iran-currency denomination selected by the user.
 */
export function useDisplayPreferences(locale: AppLocale) {
  const snapshot = useSyncExternalStore(
    subscribe,
    () => `${getDisplayCalendarPreference(locale)}|${getDigitSystemPreference(locale)}|${getIranCurrencyDisplayPreference()}`,
    () => `${getCalendarForLocale(locale)}|${getDigitSystemForLocale(locale)}|rial`,
  );
  const [calendar, digits, iranCurrency] = snapshot.split('|') as [
    ReturnType<typeof getDisplayCalendarPreference>,
    ReturnType<typeof getDigitSystemPreference>,
    ReturnType<typeof getIranCurrencyDisplayPreference>,
  ];

  return {
    calendar,
    digits,
    iranCurrency,
    setCalendar: setDisplayCalendarPreference,
    setDigits: setDigitSystemPreference,
    setIranCurrency: setIranCurrencyDisplayPreference,
  };
}
